/**
 * Compact local experience log for offline learning.
 * Situation features + action + a short outcome. Never a chat or email dump.
 */
import type { CharterRequest, Mission } from "./types";
import type { CostLineCategory } from "./opsLedger";
import { COST_LINE_CATEGORIES } from "./opsLedger";

export const EXPERIENCE_LOG_CAP = 1000;

export const EXPERIENCE_ACTIONS = [
  "accept",
  "accept_assign",
  "decline",
  "edit",
  "ferry_convert",
  "weather_override",
  "reschedule",
  "reassign_tail",
  "reassign_crew",
  "flag_review",
] as const;

export type ExperienceAction = (typeof EXPERIENCE_ACTIONS)[number];

export type ExperiencePaxBand = "" | "0" | "1-3" | "4-8" | "9+";
export type ExperienceHourBand = "" | "day" | "night";

export type ExperienceCostBand = "" | "unset" | "posted";
export type ExperienceCrewBand = "" | "dual" | "single";
export type ExperienceActor = "" | "dispatcher" | "grok" | "pilot" | "classic";

export type ExperienceFeatures = {
  originIcao: string;
  destIcao: string;
  flightType: string;
  paxBand: ExperiencePaxBand;
  hourBand: ExperienceHourBand;
  aircraftId: string;
  icaoType: string;
  declineReasonId: string;
  /** Desk cost ledger has lines for this ticket, or was consulted and empty. */
  costBand: ExperienceCostBand;
  crewBand: ExperienceCrewBand;
};

/** Compact trip / cost / crew slice. Same field names the ops snapshot uses, without memos or mail. */
export type ExperienceContext = {
  actor: ExperienceActor;
  trip: {
    id: string;
    flightNumber: string;
    flightType: string;
    originIcao: string;
    destIcao: string;
    departureIso: string;
    status: string;
    registration: string;
    aircraftId: string;
    paxCount: number;
  } | null;
  costLedger: {
    posted: boolean;
    categories: CostLineCategory[];
  };
  crew: {
    id: string;
    dutyStatus: string;
    complement: ExperienceCrewBand;
  };
};

export type ExperienceRecord = {
  id: string;
  atUtc: string;
  action: ExperienceAction;
  features: ExperienceFeatures;
  /** Short tag such as `accepted` or `declined:weather`. Not free-form chat. */
  outcome: string;
  requestId?: string;
  missionId?: string;
  context?: ExperienceContext;
  /** Same bands as the Inbox mint memory. Empty when the leg has no distance. */
  distanceBand?: string;
  slipMin?: number;
  clashKind?: string;
  chosenCrewId?: string;
  /** Company decline id, or schedule/crew/aircraft/weather/price/other. Never free text. */
  reasonCode?: string;
  /** Classic counterfactual. Distill ignores these rows. */
  shadow?: boolean;
};

const FEATURE_KEYS = [
  "originIcao",
  "destIcao",
  "flightType",
  "paxBand",
  "hourBand",
  "aircraftId",
  "icaoType",
  "declineReasonId",
  "costBand",
  "crewBand",
] as const satisfies readonly (keyof ExperienceFeatures)[];

export function emptyExperienceFeatures(): ExperienceFeatures {
  return {
    originIcao: "",
    destIcao: "",
    flightType: "",
    paxBand: "",
    hourBand: "",
    aircraftId: "",
    icaoType: "",
    declineReasonId: "",
    costBand: "",
    crewBand: "",
  };
}

export function paxBandFor(count: unknown): ExperiencePaxBand {
  const n = typeof count === "number" ? count : Number(count);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n === 0) return "0";
  if (n <= 3) return "1-3";
  if (n <= 8) return "4-8";
  return "9+";
}

/** Local hour of the ISO stamp is not used — day is 06–18 UTC. Unknown stays blank. */
export function hourBandFor(iso: unknown): ExperienceHourBand {
  if (typeof iso !== "string" || !iso.trim()) return "";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const hour = new Date(t).getUTCHours();
  return hour >= 6 && hour < 18 ? "day" : "night";
}

function icao(raw: unknown): string {
  return String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 4);
}

function token(raw: unknown, max: number): string {
  return String(raw ?? "").trim().slice(0, max);
}

function isAction(raw: unknown): raw is ExperienceAction {
  return typeof raw === "string" && (EXPERIENCE_ACTIONS as readonly string[]).includes(raw);
}

function isPaxBand(raw: unknown): raw is ExperiencePaxBand {
  return raw === "" || raw === "0" || raw === "1-3" || raw === "4-8" || raw === "9+";
}

function isHourBand(raw: unknown): raw is ExperienceHourBand {
  return raw === "" || raw === "day" || raw === "night";
}

function isCostBand(raw: unknown): raw is ExperienceCostBand {
  return raw === "" || raw === "unset" || raw === "posted";
}

function isCrewBand(raw: unknown): raw is ExperienceCrewBand {
  return raw === "" || raw === "dual" || raw === "single";
}

function isActor(raw: unknown): raw is ExperienceActor {
  return raw === "" || raw === "dispatcher" || raw === "grok" || raw === "pilot" || raw === "classic";
}

function cleanOutcome(raw: unknown): string {
  const text = token(raw, 80)
    .replace(/xai-\S+/gi, "")
    .replace(/bearer\s+\S+/gi, "")
    .replace(/logon=\S+/gi, "")
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "")
    .replace(/\s+/g, " ")
    .trim();
  if (/^[A-Za-z0-9][A-Za-z0-9:_-]{0,79}$/.test(text)) return text;
  const tagged = text.match(/delayed:\d{2}|cancelled:[a-z0-9_-]+|declined:[a-z0-9_-]+|converted:[a-z0-9_-]+|edited:[a-z0-9_-]+/i);
  return tagged ? tagged[0] : "";
}

function isCostCategory(raw: unknown): raw is CostLineCategory {
  return typeof raw === "string" && (COST_LINE_CATEGORIES as readonly string[]).includes(raw);
}

export function hydrateExperienceFeatures(raw: unknown): ExperienceFeatures {
  const src = raw && typeof raw === "object" ? (raw as Partial<ExperienceFeatures>) : {};
  return {
    originIcao: icao(src.originIcao),
    destIcao: icao(src.destIcao),
    flightType: token(src.flightType, 24),
    paxBand: isPaxBand(src.paxBand) ? src.paxBand : "",
    hourBand: isHourBand(src.hourBand) ? src.hourBand : "",
    aircraftId: token(src.aircraftId, 64),
    icaoType: token(src.icaoType, 8).toUpperCase(),
    declineReasonId: token(src.declineReasonId, 32).toLowerCase(),
    costBand: isCostBand(src.costBand) ? src.costBand : "",
    crewBand: isCrewBand(src.crewBand) ? src.crewBand : "",
  };
}

export function emptyExperienceContext(): ExperienceContext {
  return {
    actor: "",
    trip: null,
    costLedger: { posted: false, categories: [] },
    crew: { id: "", dutyStatus: "", complement: "" },
  };
}

export function hydrateExperienceContext(raw: unknown): ExperienceContext | null {
  if (!raw || typeof raw !== "object") return null;
  const src = raw as Partial<ExperienceContext>;
  const tripSrc = src.trip && typeof src.trip === "object" ? src.trip : null;
  const trip = tripSrc
    ? {
        id: token(tripSrc.id, 64),
        flightNumber: token(tripSrc.flightNumber, 16),
        flightType: token(tripSrc.flightType, 24),
        originIcao: icao(tripSrc.originIcao),
        destIcao: icao(tripSrc.destIcao),
        departureIso: token(tripSrc.departureIso, 40),
        status: token(tripSrc.status, 24),
        registration: token(tripSrc.registration, 16),
        aircraftId: token(tripSrc.aircraftId, 64),
        paxCount: Number.isFinite(tripSrc.paxCount) ? Math.max(0, Math.round(tripSrc.paxCount)) : 0,
      }
    : null;
  const costSrc = src.costLedger && typeof src.costLedger === "object" ? src.costLedger : null;
  const categories = Array.isArray(costSrc?.categories)
    ? costSrc.categories.filter(isCostCategory).slice(0, COST_LINE_CATEGORIES.length)
    : [];
  const crewSrc = src.crew && typeof src.crew === "object" ? src.crew : null;
  const actor = isActor(src.actor) ? src.actor : "";
  const context: ExperienceContext = {
    actor,
    trip: trip && (trip.id || trip.originIcao || trip.destIcao) ? trip : null,
    costLedger: { posted: costSrc?.posted === true, categories },
    crew: {
      id: token(crewSrc?.id, 64),
      dutyStatus: token(crewSrc?.dutyStatus, 24),
      complement: isCrewBand(crewSrc?.complement) ? crewSrc.complement : "",
    },
  };
  const blank =
    !context.actor &&
    !context.trip &&
    !context.costLedger.posted &&
    context.costLedger.categories.length === 0 &&
    !context.crew.id &&
    !context.crew.dutyStatus &&
    !context.crew.complement;
  return blank ? null : context;
}

export function compactExperienceContext(input: {
  actor?: ExperienceActor;
  mission?: Pick<
    Mission,
    | "id"
    | "flightNumber"
    | "callsign"
    | "flightType"
    | "originIcao"
    | "destIcao"
    | "departureIso"
    | "status"
    | "registration"
    | "aircraftId"
    | "paxCount"
  > | null;
  costLines?: readonly { missionId?: string; category?: string }[] | null;
  crewId?: string;
  dutyStatus?: string;
  complement?: ExperienceCrewBand;
}): ExperienceContext {
  const mission = input.mission ?? null;
  const lines = input.costLines;
  const categories: CostLineCategory[] = [];
  if (lines) {
    for (const line of lines) {
      if (mission && line.missionId && line.missionId !== mission.id) continue;
      if (!isCostCategory(line.category) || categories.includes(line.category)) continue;
      categories.push(line.category);
    }
  }
  const complement = isCrewBand(input.complement) ? input.complement : "";
  return {
    actor: isActor(input.actor) ? input.actor : "",
    trip: mission
      ? {
          id: token(mission.id, 64),
          flightNumber: token(mission.flightNumber || mission.callsign, 16),
          flightType: token(mission.flightType, 24),
          originIcao: icao(mission.originIcao),
          destIcao: icao(mission.destIcao),
          departureIso: token(mission.departureIso, 40),
          status: token(mission.status, 24),
          registration: token(mission.registration, 16),
          aircraftId: token(mission.aircraftId, 64),
          paxCount: Number.isFinite(mission.paxCount) ? Math.max(0, Math.round(mission.paxCount)) : 0,
        }
      : null,
    costLedger: { posted: lines != null, categories },
    crew: {
      id: token(input.crewId, 64),
      dutyStatus: token(input.dutyStatus, 24),
      complement,
    },
  };
}

export function hydrateExperienceRecord(raw: unknown): ExperienceRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const src = raw as Partial<ExperienceRecord>;
  const id = token(src.id, 80);
  const atUtc = token(src.atUtc, 40);
  if (!id || !atUtc || !isAction(src.action)) return null;
  const requestId = token(src.requestId, 64);
  const missionId = token(src.missionId, 64);
  const outcome = cleanOutcome(src.outcome);
  const context = hydrateExperienceContext(src.context);
  const distanceBand = token((src as { distanceBand?: unknown }).distanceBand, 16);
  const clashKind = token((src as { clashKind?: unknown }).clashKind, 32);
  const chosenCrewId = token((src as { chosenCrewId?: unknown }).chosenCrewId, 64);
  const reasonCode = token((src as { reasonCode?: unknown }).reasonCode, 32).toLowerCase();
  const slipRaw = (src as { slipMin?: unknown }).slipMin;
  const slipMin = typeof slipRaw === "number" && Number.isFinite(slipRaw) ? Math.round(slipRaw) : undefined;
  const shadow = (src as { shadow?: unknown }).shadow === true;
  return {
    id,
    atUtc,
    action: src.action,
    features: hydrateExperienceFeatures(src.features),
    outcome,
    ...(requestId ? { requestId } : {}),
    ...(missionId ? { missionId } : {}),
    ...(context ? { context } : {}),
    ...(distanceBand ? { distanceBand } : {}),
    ...(slipMin != null ? { slipMin } : {}),
    ...(clashKind ? { clashKind } : {}),
    ...(chosenCrewId ? { chosenCrewId } : {}),
    ...(reasonCode ? { reasonCode } : {}),
    ...(shadow ? { shadow } : {}),
  };
}

const SERIAL_KEYS = ["id", "atUtc", "action", "features", "outcome", "requestId", "missionId", "context", "distanceBand", "slipMin", "clashKind", "chosenCrewId", "reasonCode", "shadow"] as const;

/** Whitelist only. A hand-edited row cannot smuggle a prompt or a key through the serializer. */
export function serializeExperienceRecord(raw: unknown): Record<string, unknown> | null {
  const row = hydrateExperienceRecord(raw);
  if (!row) return null;
  const out: Record<string, unknown> = {};
  for (const key of SERIAL_KEYS) {
    const value = row[key];
    if (value == null || value === "" || value === false) continue;
    out[key] = value;
  }
  return out;
}

export function hydrateExperienceLog(raw: unknown): ExperienceRecord[] {
  if (!Array.isArray(raw)) return [];
  const out: ExperienceRecord[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    const record = hydrateExperienceRecord(row);
    if (!record || seen.has(record.id)) continue;
    seen.add(record.id);
    out.push(record);
  }
  return out.slice(-EXPERIENCE_LOG_CAP);
}

export function featureOverlap(
  probe: Partial<ExperienceFeatures> | null | undefined,
  past: Partial<ExperienceFeatures> | null | undefined,
): number {
  const left = hydrateExperienceFeatures(probe);
  const right = hydrateExperienceFeatures(past);
  let n = 0;
  for (const key of FEATURE_KEYS) {
    if (!left[key] || !right[key]) continue;
    if (left[key] === right[key]) n += 1;
  }
  return n;
}

/** How many probe features are filled. Distance from a past row is this minus overlap. */
export function probeFeatureCount(probe: Partial<ExperienceFeatures> | null | undefined): number {
  const left = hydrateExperienceFeatures(probe);
  return FEATURE_KEYS.filter((key) => Boolean(left[key])).length;
}

export function featureDistance(
  probe: Partial<ExperienceFeatures> | null | undefined,
  past: Partial<ExperienceFeatures> | null | undefined,
): number {
  return Math.max(0, probeFeatureCount(probe) - featureOverlap(probe, past));
}

export type ExperienceAppendInput = {
  action: ExperienceAction;
  features: Partial<ExperienceFeatures>;
  outcome: string;
  requestId?: string;
  missionId?: string;
  id?: string;
  atUtc?: string;
  context?: ExperienceContext;
  distanceBand?: string;
  slipMin?: number;
  clashKind?: string;
  chosenCrewId?: string;
  reasonCode?: string;
  shadow?: boolean;
};

function newExperienceId(action: ExperienceAction, requestId: string, atUtc: string): string {
  if (requestId) return `exp-${requestId}-${action}`;
  return `exp-${action}-${atUtc}`;
}

/**
 * Append-only. The same request + action is kept once (first write wins),
 * except edits, which stay as separate rows. Drops the oldest rows past the cap.
 */
export function appendExperience(
  log: readonly ExperienceRecord[] | null | undefined,
  input: ExperienceAppendInput,
  now = new Date(),
): ExperienceRecord[] {
  const current = hydrateExperienceLog(log);
  const atUtc = token(input.atUtc, 40) || now.toISOString();
  const requestId = token(input.requestId, 64);
  const action = input.action;
  const actor = input.context?.actor ?? "";
  const shadow = input.shadow === true;
  if (
    action !== "edit" &&
    requestId &&
    current.some((row) => row.requestId === requestId && row.action === action && (row.context?.actor ?? "") === actor && Boolean(row.shadow) === shadow)
  ) {
    return current;
  }
  const missionId = token(input.missionId, 64);
  const context = hydrateExperienceContext(input.context);
  const record: ExperienceRecord = {
    id: token(input.id, 80) || (action === "edit" ? `exp-edit-${missionId || requestId || "row"}-${atUtc}` : newExperienceId(action, requestId, atUtc)),
    atUtc,
    action,
    features: hydrateExperienceFeatures(input.features),
    outcome: cleanOutcome(input.outcome),
    ...(requestId ? { requestId } : {}),
    ...(missionId ? { missionId } : {}),
    ...(context ? { context } : {}),
    ...(token(input.distanceBand, 16) ? { distanceBand: token(input.distanceBand, 16) } : {}),
    ...(typeof input.slipMin === "number" && Number.isFinite(input.slipMin) ? { slipMin: Math.round(input.slipMin) } : {}),
    ...(token(input.clashKind, 32) ? { clashKind: token(input.clashKind, 32) } : {}),
    ...(token(input.chosenCrewId, 64) ? { chosenCrewId: token(input.chosenCrewId, 64) } : {}),
    ...(token(input.reasonCode, 32) ? { reasonCode: token(input.reasonCode, 32).toLowerCase() } : {}),
    ...(input.shadow ? { shadow: true } : {}),
  };
  return hydrateExperienceLog([...current, record]);
}

export function experienceFromInbox(
  row: Pick<
    CharterRequest,
    "id" | "originIcao" | "destIcao" | "flightType" | "paxCount" | "departureIso" | "suggestedAircraftId"
  >,
  action: "accept" | "decline",
  opts?: {
    now?: Date;
    missionId?: string;
    declineReasonId?: string;
    icaoType?: string;
    context?: ExperienceContext;
  },
): ExperienceAppendInput {
  const reason = token(opts?.declineReasonId, 32).toLowerCase();
  const context = opts?.context ? hydrateExperienceContext(opts.context) ?? undefined : undefined;
  return {
    action,
    requestId: row.id,
    missionId: opts?.missionId,
    atUtc: (opts?.now ?? new Date()).toISOString(),
    outcome: action === "decline" && reason ? `declined:${reason}` : action === "decline" ? "declined" : "accepted",
    context,
    features: {
      originIcao: row.originIcao,
      destIcao: row.destIcao,
      flightType: row.flightType,
      paxBand: paxBandFor(row.paxCount),
      hourBand: hourBandFor(row.departureIso),
      aircraftId: row.suggestedAircraftId,
      icaoType: opts?.icaoType ?? "",
      declineReasonId: action === "decline" ? reason : "",
      costBand: context?.costLedger.posted ? (context.costLedger.categories.length ? "posted" : "unset") : context ? "unset" : "",
      crewBand: context?.crew.complement ?? "",
    },
  };
}

export function experienceFromMission(
  mission: Pick<
    Mission,
    "id" | "inboxRequestId" | "originIcao" | "destIcao" | "flightType" | "paxCount" | "departureIso" | "aircraftId"
  >,
  action: "edit" | "ferry_convert" | "decline",
  opts: {
    outcome: string;
    now?: Date;
    declineReasonId?: string;
    icaoType?: string;
    context?: ExperienceContext;
    id?: string;
  },
): ExperienceAppendInput {
  const reason = token(opts.declineReasonId, 32).toLowerCase();
  const context = opts.context ? hydrateExperienceContext(opts.context) ?? undefined : undefined;
  const atUtc = (opts.now ?? new Date()).toISOString();
  return {
    action,
    id: opts.id,
    requestId: mission.inboxRequestId,
    missionId: mission.id,
    atUtc,
    outcome: token(opts.outcome, 80),
    context,
    features: {
      originIcao: mission.originIcao,
      destIcao: mission.destIcao,
      flightType: mission.flightType,
      paxBand: paxBandFor(mission.paxCount),
      hourBand: hourBandFor(mission.departureIso),
      aircraftId: mission.aircraftId,
      icaoType: opts.icaoType ?? "",
      declineReasonId: action === "decline" ? reason : "",
      costBand: context?.costLedger.posted ? (context.costLedger.categories.length ? "posted" : "unset") : context ? "unset" : "",
      crewBand: context?.crew.complement ?? "",
    },
  };
}

export type ExperienceHit = {
  record: ExperienceRecord;
  overlap: number;
  distance: number;
};

export function queryExperience(
  log: readonly ExperienceRecord[] | null | undefined,
  features: Partial<ExperienceFeatures>,
  opts?: { action?: ExperienceAction; limit?: number },
): ExperienceHit[] {
  const limit = Math.max(1, Math.min(50, opts?.limit ?? 8));
  const hits: ExperienceHit[] = [];
  for (const record of hydrateExperienceLog(log)) {
    if (opts?.action && record.action !== opts.action) continue;
    const overlap = featureOverlap(features, record.features);
    if (overlap <= 0) continue;
    hits.push({ record, overlap, distance: featureDistance(features, record.features) });
  }
  hits.sort((a, b) => b.overlap - a.overlap || b.record.atUtc.localeCompare(a.record.atUtc));
  return hits.slice(0, limit);
}

export type ExperienceScore = {
  /** Sum of accept overlaps minus decline overlaps. Higher favors past accepts. */
  score: number;
  acceptOverlap: number;
  declineOverlap: number;
  /** Similar past accepts, nearest first. */
  rankedAccepts: ExperienceHit[];
  /** Past declines, nearest first (distance 0 matches the probe). */
  rankedDeclines: ExperienceHit[];
};

/**
 * Rank by feature overlap with past accepts, and by distance from past declines.
 * Not a model — shared tokens only.
 */
export function scoreExperience(
  log: readonly ExperienceRecord[] | null | undefined,
  features: Partial<ExperienceFeatures>,
): ExperienceScore {
  const rankedAccepts = queryExperience(log, features, { action: "accept", limit: 8 });
  const rankedDeclines = queryExperience(log, features, { action: "decline", limit: 8 }).sort(
    (a, b) => a.distance - b.distance || b.overlap - a.overlap,
  );
  const acceptOverlap = rankedAccepts.reduce((sum, hit) => sum + hit.overlap, 0);
  const declineOverlap = rankedDeclines.reduce((sum, hit) => sum + hit.overlap, 0);
  return {
    score: acceptOverlap - declineOverlap,
    acceptOverlap,
    declineOverlap,
    rankedAccepts,
    rankedDeclines,
  };
}
